import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  PersonAdd,
  Edit,
  Security,
  AdminPanelSettings,
  Person,
  Search,
  Refresh,
  Star,
  RemoveModerator,
  Check,
  Close,
  Group,
  DeleteSweep,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { usePermissions } from '../../context/PermissionContext';
import { AdminGuard } from '../../components/PermissionGuard';
import API_BASE_URL from '../../config/api';

const UserManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAdmin, isOwner } = usePermissions();

  // State
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [interfaces, setInterfaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  // Bulk edit state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [bulkRoles, setBulkRoles] = useState([]);
  const [bulkAction, setBulkAction] = useState('assign'); // 'assign' or 'remove'
  const [bulkLoading, setBulkLoading] = useState(false);

  // Fetch data
  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/AccessControl/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.users || []);
      }
    } catch (err) {
      setError('Failed to fetch users');
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/AccessControl/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success) {
        setRoles(data.roles || []);
      }
    } catch (err) {
      console.error('Failed to fetch roles', err);
    }
  }, []);

  const fetchInterfaces = useCallback(async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/AccessControl/interfaces`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success) {
        setInterfaces(data.interfaces || []);
      }
    } catch (err) {
      console.error('Failed to fetch interfaces', err);
    }
  }, []);

  const fetchUserDetails = useCallback(async (userNo) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/AccessControl/users/${userNo}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success) {
        setSelectedUserDetails(data);
        setUserRoles(data.roles?.map(r => r.roleId) || []);
      }
    } catch (err) {
      setError('Failed to fetch user details');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchRoles(), fetchInterfaces()]);
      setLoading(false);
    };
    loadData();
  }, [fetchUsers, fetchRoles, fetchInterfaces]);

  // Handlers
  const handleEditUser = (user) => {
    setSelectedUser(user);
    fetchUserDetails(user.userNo);
    setEditDialogOpen(true);
    setTabValue(0);
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setSelectedUserDetails(null);
    setUserRoles([]);
  };

  const handleRoleChange = async (roleId, isAssigned) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const method = isAssigned ? 'DELETE' : 'POST';
      const response = await fetch(
        `${API_BASE_URL}/api/AccessControl/users/${selectedUser.userNo}/roles/${roleId}`,
        {
          method,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess(isAssigned ? 'Role removed successfully' : 'Role assigned successfully');
        fetchUserDetails(selectedUser.userNo);
        fetchUsers();
      } else {
        setError(data.message || 'Failed to update role');
      }
    } catch (err) {
      setError('Failed to update role');
    }
  };

  const handlePromoteToAdmin = async (userNo) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(
        `${API_BASE_URL}/api/AccessControl/users/${userNo}/promote`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('User promoted to admin successfully');
        fetchUsers();
        if (selectedUser?.userNo === userNo) {
          fetchUserDetails(userNo);
        }
      } else {
        setError(data.message || 'Failed to promote user');
      }
    } catch (err) {
      setError('Failed to promote user');
    }
  };

  const handleDemoteFromAdmin = async (userNo) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(
        `${API_BASE_URL}/api/AccessControl/users/${userNo}/demote`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('User demoted from admin successfully');
        fetchUsers();
        if (selectedUser?.userNo === userNo) {
          fetchUserDetails(userNo);
        }
      } else {
        setError(data.message || 'Failed to demote user');
      }
    } catch (err) {
      setError('Failed to demote user');
    }
  };

  const handleGrantInterface = async (interfaceId) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(
        `${API_BASE_URL}/api/AccessControl/users/${selectedUser.userNo}/interfaces/${interfaceId}/grant`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reason: 'Granted by admin' }),
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('Interface granted successfully');
        fetchUserDetails(selectedUser.userNo);
      } else {
        setError(data.message || 'Failed to grant interface');
      }
    } catch (err) {
      setError('Failed to grant interface');
    }
  };

  const handleRevokeInterface = async (interfaceId) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(
        `${API_BASE_URL}/api/AccessControl/users/${selectedUser.userNo}/interfaces/${interfaceId}/revoke`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reason: 'Revoked by admin' }),
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('Interface revoked successfully');
        fetchUserDetails(selectedUser.userNo);
      } else {
        setError(data.message || 'Failed to revoke interface');
      }
    } catch (err) {
      setError('Failed to revoke interface');
    }
  };

  // Remove a single interface override (clears the grant/revoke override, returns to role-based access)
  const handleRemoveOverride = async (interfaceId) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(
        `${API_BASE_URL}/api/AccessControl/users/${selectedUser.userNo}/interfaces/${interfaceId}/override`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('Override removed successfully');
        fetchUserDetails(selectedUser.userNo);
      } else {
        setError(data.message || 'Failed to remove override');
      }
    } catch (err) {
      setError('Failed to remove override');
    }
  };

  // Remove all interface overrides for a user
  const handleRemoveAllOverrides = async () => {
    if (!selectedUserDetails?.overrides?.length) return;

    try {
      const token = localStorage.getItem('jwtToken');
      let successCount = 0;
      let failCount = 0;

      for (const override of selectedUserDetails.overrides) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/AccessControl/users/${selectedUser.userNo}/interfaces/${override.interfaceId}/override`,
            {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
          const data = await response.json();
          if (data.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
      }

      if (failCount === 0) {
        setSuccess(`Successfully removed all ${successCount} overrides`);
      } else {
        setError(`Removed ${successCount} overrides, ${failCount} failed`);
      }
      fetchUserDetails(selectedUser.userNo);
    } catch (err) {
      setError('Failed to remove overrides');
    }
  };

  // Bulk edit handlers
  const handleSelectUser = (userNo) => {
    setSelectedUsers(prev => {
      if (prev.includes(userNo)) {
        return prev.filter(id => id !== userNo);
      } else {
        return [...prev, userNo];
      }
    });
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.userNo));
    }
  };

  const handleOpenBulkEdit = () => {
    setBulkRoles([]);
    setBulkAction('assign');
    setBulkEditDialogOpen(true);
  };

  const handleCloseBulkEdit = () => {
    setBulkEditDialogOpen(false);
    setBulkRoles([]);
    setBulkAction('assign');
  };

  const handleBulkRoleToggle = (roleId) => {
    setBulkRoles(prev => {
      if (prev.includes(roleId)) {
        return prev.filter(id => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  const handleBulkRoleChange = async () => {
    if (bulkRoles.length === 0) {
      setError('Please select at least one role');
      return;
    }

    setBulkLoading(true);
    const token = localStorage.getItem('jwtToken');
    let successCount = 0;
    let failCount = 0;

    for (const userNo of selectedUsers) {
      for (const roleId of bulkRoles) {
        try {
          const method = bulkAction === 'assign' ? 'POST' : 'DELETE';
          const response = await fetch(
            `${API_BASE_URL}/api/AccessControl/users/${userNo}/roles/${roleId}`,
            {
              method,
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
          const data = await response.json();
          if (data.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
      }
    }

    setBulkLoading(false);
    handleCloseBulkEdit();
    setSelectedUsers([]);
    fetchUsers();

    if (failCount === 0) {
      setSuccess(`Successfully ${bulkAction === 'assign' ? 'assigned' : 'removed'} roles for ${selectedUsers.length} users`);
    } else {
      setError(`Completed with ${failCount} errors. ${successCount} operations succeeded.`);
    }
  };

  // Filter users by search term
  const filteredUsers = users.filter(user =>
    user.emailID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <AdminGuard>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          User Management
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Manage user roles and permissions
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Search and Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: { xs: 1, md: 2 }, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            <TextField
              placeholder="Search users..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: { xs: '100%', sm: 300 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            {selectedUsers.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={`${selectedUsers.length} selected`}
                  color="primary"
                  onDelete={() => setSelectedUsers([])}
                />
                <Button
                  variant="contained"
                  startIcon={<Group />}
                  onClick={handleOpenBulkEdit}
                  size="small"
                >
                  Bulk Edit Roles
                </Button>
              </Box>
            )}
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchUsers();
              fetchRoles();
            }}
          >
            Refresh
          </Button>
        </Box>

        {/* Users Table */}
        <TableContainer component={Paper} elevation={2} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow sx={{
                backgroundColor: theme.palette.mode === 'dark'
                  ? theme.palette.grey[800]
                  : theme.palette.grey[100]
              }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
                    checked={filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                    onChange={handleSelectAllUsers}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Roles</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow
                    key={user.userNo}
                    hover
                    selected={selectedUsers.includes(user.userNo)}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(144, 202, 249, 0.08)'
                          : 'rgba(25, 118, 210, 0.08)',
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(144, 202, 249, 0.12)'
                          : 'rgba(25, 118, 210, 0.12)',
                      },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedUsers.includes(user.userNo)}
                        onChange={() => handleSelectUser(user.userNo)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {user.isOwner ? (
                          <Star sx={{ color: 'gold' }} />
                        ) : user.isAdmin ? (
                          <AdminPanelSettings color="primary" />
                        ) : (
                          <Person color="action" />
                        )}
                        <Typography>
                          {user.firstName} {user.lastName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: { xs: 160, md: 'none' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.emailID}</TableCell>
                    <TableCell>
                      {user.roles ? (
                        user.roles.split(', ').map((role, idx) => (
                          <Chip
                            key={idx}
                            label={role}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No roles
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.isOwner && (
                        <Chip label="Owner" color="warning" size="small" sx={{ mr: 0.5 }} />
                      )}
                      {user.isAdmin && !user.isOwner && (
                        <Chip label="Admin" color="primary" size="small" sx={{ mr: 0.5 }} />
                      )}
                      <Chip
                        label={user.status || 'Active'}
                        color={user.status === 'active' ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit User Permissions">
                        <IconButton onClick={() => handleEditUser(user)} size="small">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {isOwner && !user.isOwner && (
                        user.isAdmin ? (
                          <Tooltip title="Demote from Admin">
                            <IconButton
                              onClick={() => handleDemoteFromAdmin(user.userNo)}
                              size="small"
                              color="warning"
                            >
                              <RemoveModerator fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Promote to Admin">
                            <IconButton
                              onClick={() => handlePromoteToAdmin(user.userNo)}
                              size="small"
                              color="primary"
                            >
                              <AdminPanelSettings />
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Edit User Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            Edit User: {selectedUser?.firstName} {selectedUser?.lastName}
          </DialogTitle>
          <DialogContent>
            {!selectedUserDetails ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                  <Tab label="Roles" />
                  <Tab label="Interface Overrides" />
                </Tabs>

                {tabValue === 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Assign Roles
                    </Typography>
                    <Grid container spacing={1}>
                      {roles.map((role) => {
                        const isAssigned = selectedUserDetails.roles?.some(
                          (r) => r.roleId === role.roleId
                        );
                        return (
                          <Grid item xs={12} sm={6} key={role.roleId}>
                            <Card
                              variant="outlined"
                              sx={{
                                cursor: 'pointer',
                                backgroundColor: isAssigned
                                  ? theme.palette.mode === 'dark'
                                    ? 'rgba(144, 202, 249, 0.16)'
                                    : 'rgba(25, 118, 210, 0.08)'
                                  : 'transparent',
                                borderColor: isAssigned
                                  ? theme.palette.primary.main
                                  : theme.palette.divider,
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.08)'
                                    : 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                              onClick={() => handleRoleChange(role.roleId, isAssigned)}
                            >
                              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Box>
                                    <Typography variant="subtitle2">
                                      {role.roleName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {role.description}
                                    </Typography>
                                  </Box>
                                  {isAssigned && <Check color="primary" />}
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}

                {tabValue === 1 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Interface Overrides
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Grant or revoke specific interfaces independently of roles
                    </Typography>

                    {/* Current Overrides */}
                    {selectedUserDetails.overrides?.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2">
                            Current Overrides ({selectedUserDetails.overrides.length})
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteSweep />}
                            onClick={handleRemoveAllOverrides}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Remove All
                          </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selectedUserDetails.overrides.map((override) => (
                            <Chip
                              key={override.interfaceId}
                              label={`${override.interfaceName} (${override.isGranted ? 'Granted' : 'Revoked'})`}
                              color={override.isGranted ? 'success' : 'error'}
                              size="small"
                              onDelete={() => handleRemoveOverride(override.interfaceId)}
                              deleteIcon={
                                <Close
                                  sx={{
                                    fontSize: '16px !important',
                                    '&:hover': { color: 'inherit' }
                                  }}
                                />
                              }
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Grant/Revoke Interfaces - Grouped by Category */}
                    {(() => {
                      // Canonical display names for known categories
                      const categoryDisplayNames = {
                        'pdf': 'PDF',
                        'excel': 'Excel',
                        'links': 'Links',
                        'other': 'Other',
                        'ai': 'AI',
                        'analytics': 'Analytics',
                      };

                      // Group interfaces by normalized (lowercase) category
                      const groupedInterfaces = interfaces.reduce((acc, iface) => {
                        const rawCategory = (iface.category || 'Other').trim();
                        const normalizedKey = rawCategory.toLowerCase();
                        if (!acc[normalizedKey]) {
                          // Use canonical display name if known, otherwise capitalize the raw value
                          acc[normalizedKey] = {
                            displayName: categoryDisplayNames[normalizedKey] || rawCategory.charAt(0).toUpperCase() + rawCategory.slice(1),
                            items: [],
                          };
                        }
                        acc[normalizedKey].items.push(iface);
                        return acc;
                      }, {});

                      // Define category order (lowercase keys)
                      const categoryOrder = ['pdf', 'excel', 'links', 'other', 'ai', 'analytics'];

                      // Sort categories
                      const sortedCategories = Object.keys(groupedInterfaces).sort((a, b) => {
                        const aIndex = categoryOrder.indexOf(a);
                        const bIndex = categoryOrder.indexOf(b);
                        if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
                        if (aIndex === -1) return 1;
                        if (bIndex === -1) return -1;
                        return aIndex - bIndex;
                      });

                      return sortedCategories.map((categoryKey) => (
                        <Box key={categoryKey} sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {groupedInterfaces[categoryKey].displayName}
                          </Typography>
                          <Grid container spacing={1}>
                            {groupedInterfaces[categoryKey].items.map((iface) => {
                              const hasAccess = selectedUserDetails.interfaces?.some(
                                (i) => i.interfaceId === iface.interfaceId
                              );
                              return (
                                <Grid item xs={12} sm={6} md={4} key={iface.interfaceId}>
                                  <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography variant="body2" noWrap>
                                            {iface.interfaceName}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                          {hasAccess ? (
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => handleRevokeInterface(iface.interfaceId)}
                                              title="Revoke Access"
                                            >
                                              <Close fontSize="small" />
                                            </IconButton>
                                          ) : (
                                            <IconButton
                                              size="small"
                                              color="success"
                                              onClick={() => handleGrantInterface(iface.interfaceId)}
                                              title="Grant Access"
                                            >
                                              <Check fontSize="small" />
                                            </IconButton>
                                          )}
                                        </Box>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      ));
                    })()}
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Edit Dialog */}
        <Dialog
          open={bulkEditDialogOpen}
          onClose={handleCloseBulkEdit}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Group color="primary" />
              Bulk Edit Roles
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
              </Alert>

              <Typography variant="subtitle2" gutterBottom>
                Action
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <Button
                  variant={bulkAction === 'assign' ? 'contained' : 'outlined'}
                  onClick={() => setBulkAction('assign')}
                  startIcon={<Check />}
                  color="success"
                >
                  Assign Roles
                </Button>
                <Button
                  variant={bulkAction === 'remove' ? 'contained' : 'outlined'}
                  onClick={() => setBulkAction('remove')}
                  startIcon={<Close />}
                  color="error"
                >
                  Remove Roles
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Select Roles to {bulkAction === 'assign' ? 'Assign' : 'Remove'}
              </Typography>
              <Grid container spacing={1}>
                {roles.map((role) => {
                  const isSelected = bulkRoles.includes(role.roleId);
                  return (
                    <Grid item xs={12} sm={6} key={role.roleId}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: isSelected
                            ? theme.palette.mode === 'dark'
                              ? bulkAction === 'assign'
                                ? 'rgba(102, 187, 106, 0.16)'
                                : 'rgba(244, 67, 54, 0.16)'
                              : bulkAction === 'assign'
                                ? 'rgba(102, 187, 106, 0.08)'
                                : 'rgba(244, 67, 54, 0.08)'
                            : 'transparent',
                          borderColor: isSelected
                            ? bulkAction === 'assign'
                              ? theme.palette.success.main
                              : theme.palette.error.main
                            : theme.palette.divider,
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.08)'
                              : 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                        onClick={() => handleBulkRoleToggle(role.roleId)}
                      >
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography variant="subtitle2">
                                {role.roleName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {role.description || 'No description'}
                              </Typography>
                            </Box>
                            {isSelected && (
                              <Check color={bulkAction === 'assign' ? 'success' : 'error'} />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBulkEdit}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleBulkRoleChange}
              disabled={bulkRoles.length === 0 || bulkLoading}
              color={bulkAction === 'assign' ? 'success' : 'error'}
              startIcon={bulkLoading ? <CircularProgress size={20} /> : (bulkAction === 'assign' ? <Check /> : <Close />)}
            >
              {bulkLoading
                ? 'Processing...'
                : `${bulkAction === 'assign' ? 'Assign' : 'Remove'} ${bulkRoles.length} Role${bulkRoles.length !== 1 ? 's' : ''}`
              }
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminGuard>
  );
};

export default UserManagement;
